import { create } from 'zustand'

/**
 * Sidebar expand/collapse state — one source of truth shared by
 * SuperAdminLayout, AdminLayout, and UserLayout so the user's
 * preference is honoured everywhere they navigate.
 *
 * Persisted to localStorage (not server) because:
 *   - It's a UI display preference, not a user account property.
 *   - Per-device behaviour is the right default (a wide monitor at
 *     work and a small laptop at home shouldn't have to share the
 *     same collapsed/expanded state).
 *   - Zero round-trip cost on every page load.
 *
 * On SSR / non-browser environments the localStorage read is guarded
 * so the module load never throws.
 */

const STORAGE_KEY = 'taskmagix.sidebar.collapsed'

function readInitial() {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persist(collapsed) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
  } catch {
    /* quota / privacy mode — ignore */
  }
}

export const useSidebarStore = create((set, get) => ({
  collapsed: readInitial(),
  toggle() {
    const next = !get().collapsed
    persist(next)
    set({ collapsed: next })
  },
  set(value) {
    const next = Boolean(value)
    persist(next)
    set({ collapsed: next })
  },

  // Mobile off-canvas drawer — transient (NOT persisted): the sidebar is an
  // in-flow panel on desktop (lg+) and a slide-in drawer on smaller screens.
  // Shared here so all three layouts + the header hamburger stay in sync.
  mobileOpen: false,
  openMobile() {
    set({ mobileOpen: true })
  },
  closeMobile() {
    set({ mobileOpen: false })
  },
}))

/** Selector hook — most call sites only need the boolean.
 *  Returns just the `collapsed` flag to keep render dependencies
 *  tight (changing the toggle action wouldn't re-render readers). */
export function useSidebarCollapsed() {
  return useSidebarStore((s) => s.collapsed)
}
